import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  SimpleGrid,
  GridItem,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import axiosInstance from "../../../helpers/axiosInstance";
import Translate from "../../../components/LanguageTranslation/Translate";

export default function AddOtherTransportation({
  isOpen,
  onClose,
  addTripItem,
  defaultSubcategories = [],
  editItem = null,
  onEditSuccess = null,
}) {
  // you can inspect defaultSubcategories.includes("other_transportation") if needed
  const [company, setCompany] = useState("");
  const [startLocation, setStartLocation] = useState("");
  const [endLocation, setEndLocation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Populate form with existing data for editing
        setCompany(editItem.company || "");
        setStartLocation(editItem.start_location || "");
        setEndLocation(editItem.end_location || "");
        setStartDate(editItem.start_date ? new Date(editItem.start_date).toISOString().split('T')[0] : "");
        setEndDate(editItem.end_date ? new Date(editItem.end_date).toISOString().split('T')[0] : "");
        setPrice(editItem.price?.toString() || "");
        setCurrency(editItem.currency || "USD");
      } else {
        // Reset form for new other transportation
        setCompany("");
        setStartLocation("");
        setEndLocation("");
        setStartDate("");
        setEndDate("");
        setPrice("");
        setCurrency("USD");
      }
      setErrors({});
    }
  }, [isOpen, editItem]);

  const validate = () => {
    const e = {};
    if (!company.trim()) e.company = "Company name required";
    if (!startLocation.trim()) e.startLocation = "Start location required";
    if (!endLocation.trim()) e.endLocation = "End location required";
    if (!startDate) e.startDate = "Start date required";
    if (!endDate) e.endDate = "End date required";
    if (!price || isNaN(price) || price <= 0) e.price = "Valid price required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      company,
      start_location: startLocation,
      end_location: endLocation,
      start_date: new Date(startDate).toISOString(),
      end_date: new Date(endDate).toISOString(),
      price: parseFloat(price),
      currency,
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const response = await axiosInstance.post(
        "/bucket-list/add-manual-other-for-transportation",
        payload
      );
      if (response.data.success) {
        const otherTransportationData = {
          company,
          startLocation: startLocation,
          endLocation: endLocation,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
          price: parseFloat(price),
          currency,
        };

        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            data: {
              company,
              start_location: startLocation,
              end_location: endLocation,
              start_date: new Date(startDate).toISOString(),
              end_date: new Date(endDate).toISOString(),
              price: parseFloat(price),
              currency,
            },
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const newItem = {
            _id: response.data.data._id,
            data: {
              company,
              preference: "other",
              start_location: startLocation,
              end_location: endLocation,
              start_date: new Date(startDate).toISOString(),
              end_date: new Date(endDate).toISOString(),
              price: parseFloat(price),
              currency,
            },
            category: "transportation",
            preference: "other",
            email: localStorage.getItem("email"),
          };

          // 
          // Only wrap in data for 'explore', otherwise add as-is
          if (newItem.category === "explore") {
            addTripItem({ ...newItem, data: { ...newItem } });
          } else {
            addTripItem(newItem);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>
          <Translate text={editItem ? "Edit Other Transportation" : "Add Other Transportation"} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={2} spacing={4}>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.company}>
                <FormLabel>
                  <Translate text="Company" />
                </FormLabel>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.company} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.startLocation}>
                <FormLabel>
                  <Translate text="Start Location" />
                </FormLabel>
                <Input
                  value={startLocation}
                  onChange={(e) => setStartLocation(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.startLocation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.endLocation}>
                <FormLabel>
                  <Translate text="End Location" />
                </FormLabel>
                <Input
                  value={endLocation}
                  onChange={(e) => setEndLocation(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.endLocation} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.startDate}>
                <FormLabel>
                  <Translate text="Start Date" />
                </FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.startDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.endDate}>
                <FormLabel>
                  <Translate text="End Date" />
                </FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <FormErrorMessage>
                  <Translate text={errors.endDate} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl isInvalid={errors.price}>
                <FormLabel>
                  <Translate text="Price" />
                </FormLabel>
                <NumberInput value={price} onChange={(_, v) => setPrice(v)}>
                  <NumberInputField />
                </NumberInput>
                <FormErrorMessage>
                  <Translate text={errors.price} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>
                  <Translate text="Currency" />
                </FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="USD">USD $</option>
                  <option value="EUR">EUR €</option>
                  <option value="GBP">GBP £</option>
                  {/* add more as needed */}
                </Select>
              </FormControl>
            </GridItem>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="customGreen" mr={3} onClick={handleSave}>
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose}>
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
